<script setup lang="ts">
import { DateTime } from 'luxon'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AffectedElementType from '../../../../../Constants/AffectedElementType.ts'
import type { AffectedElement, ContractRestructuring } from '../../../../../Types/restructuring'
import AffectedElementListItem from './AffectedElementListItem.vue'

const props = defineProps<{
  restructuring: ContractRestructuring
}>()

const { t } = useI18n()

const affectedElements = computed<AffectedElement[]>(() => {
  const elements = []

  const simulations = props.restructuring.simulations ?? []
  for (let simulation of simulations) {
    elements.push({
      id: simulation.id,
      type: AffectedElementType.SIMULATION,
      createdAt: DateTime.fromISO(simulation.createdAt),
      label: simulation.label,
      deleted: Boolean(simulation.deleted),
      canceled: Boolean(simulation.canceled),
      canceledAt: simulation.canceled && simulation.statusAt ? DateTime.fromISO(simulation.statusAt) : null,
    })
  }

  const contracts = props.restructuring.restructuredContracts ?? []
  for (let contract of contracts) {
    elements.push({
      id: contract.id,
      type: AffectedElementType.CONTRACT,
      createdAt: DateTime.fromISO(contract.createdAt),
      label: contract.label,
      deleted: Boolean(contract.deleted),
      canceled: Boolean(contract.canceled),
      canceledAt: contract.canceled && contract.statusAt ? DateTime.fromISO(contract.statusAt) : null,
    })
  }

  return elements.sort((left, right) => left.createdAt?.toMillis() - right.createdAt?.toMillis())
})
</script>

<template>
  <table class="table table-striped table-hover table-sm">
    <thead>
      <tr>
        <th class="font-weight-bold">
          {{ t('restructuring.list.columns.elementType') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.list.columns.elementCreatedAt') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.list.columns.elementCanceledAt') }}
        </th>
        <th class="font-weight-bold">
          {{ t('restructuring.list.columns.elementName') }}
        </th>
      </tr>
    </thead>
    <tbody>
      <AffectedElementListItem
        v-for="affectedElement in affectedElements"
        :key="affectedElement.id"
        :element="affectedElement"
        data-test="affected-element-list-item"
      />
      <tr v-if="affectedElements.length === 0" data-test="affected-element-list-placeholder">
        <td colspan="4">{{ t('restructuring.detail.affected_elements.empty') }}</td>
      </tr>
    </tbody>
  </table>
</template>
